import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Card } from '../../../shared/components/ui/Card.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { Modal } from '../../../shared/components/ui/Modal.js';
import { serversApi } from '../../servers/api.js';
import { type CreatedMcpKey, type McpKeyRow, mcpApi } from '../api.js';

/**
 * Chaves do MCP (mcp.chatplusbr.com) — o que o Claude/ChatGPT/n8n apresenta pra
 * operar os clientes por ferramentas.
 *
 * Duas famílias: MASTER (qualquer cliente de qualquer servidor — pra equipe) e
 * DE CLIENTE (presa a um servidor + tenant). Escrita é opt-in. O texto da chave
 * aparece uma vez, no modal de criação; depois só o hash existe no banco.
 */

const MCP_URL = 'https://mcp.chatplusbr.com/mcp';

export function McpKeysPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [criada, setCriada] = useState<CreatedMcpKey | null>(null);
  const qc = useQueryClient();

  const keysQuery = useQuery({ queryKey: ['mcp-keys'], queryFn: () => mcpApi.list() });
  const serversQuery = useQuery({ queryKey: ['servers'], queryFn: () => serversApi.list() });
  const servidorNome = (id: number | null): string =>
    serversQuery.data?.find((s) => s.id === id)?.name ?? (id === null ? '' : `#${id}`);

  const revokeMut = useMutation({
    mutationFn: async (k: McpKeyRow) => mcpApi.revoke(k.id),
    onSuccess: (_d, k) => {
      void qc.invalidateQueries({ queryKey: ['mcp-keys'] });
      toast.success(`Chave "${k.name}" revogada`, { description: 'A próxima chamada com ela já é recusada.' });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const keys = keysQuery.data ?? [];
  const ativas = keys.filter((k) => !k.revoked_at);
  const revogadas = keys.filter((k) => k.revoked_at);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-n-900">Chaves do MCP</h1>
          <p className="text-[13px] text-n-500">
            Acesso por IA (Claude, ChatGPT, n8n) aos clientes via{' '}
            <code className="font-mono text-brand-600">{MCP_URL}</code>. Chave <b>master</b> opera qualquer
            cliente; chave <b>de cliente</b> fica presa a um só.
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus size={14} /> Nova chave
        </Button>
      </div>

      <Card>
        <table className="w-full">
          <thead className="border-b border-n-200 bg-n-50 text-left text-[11px] font-semibold uppercase text-n-500">
            <tr>
              <th className="px-5 py-3">Nome</th>
              <th className="px-5 py-3">Escopo</th>
              <th className="px-5 py-3">Permissão</th>
              <th className="px-5 py-3">Criada por</th>
              <th className="px-5 py-3">Último uso</th>
              <th className="px-5 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {keysQuery.isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-[13px] text-n-500">
                  Carregando...
                </td>
              </tr>
            )}
            {!keysQuery.isLoading && ativas.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-[13px] text-n-500">
                  Nenhuma chave ativa. Crie a primeira pra conectar uma IA.
                </td>
              </tr>
            )}
            {[...ativas, ...revogadas].map((k) => (
              <tr key={k.id} className={`border-b border-n-100 hover:bg-n-50 ${k.revoked_at ? 'opacity-50' : ''}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2 text-[13px] font-medium text-n-900">
                    <KeyRound size={13} className="text-n-400" />
                    {k.name}
                    {k.revoked_at && (
                      <span className="rounded-pill bg-n-100 px-1.5 py-0.5 text-[10px] font-semibold text-n-600">
                        REVOGADA
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-n-500">
                    criada em {new Date(k.created_at).toLocaleString('pt-BR')}
                  </div>
                </td>
                <td className="px-5 py-3">
                  {k.master ? (
                    <span className="rounded-pill bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700">
                      MASTER
                    </span>
                  ) : (
                    <div className="text-[12px] text-n-700">
                      {servidorNome(k.server_id)} · cliente <span className="font-mono">{k.tenant_id}</span>
                    </div>
                  )}
                </td>
                <td className="px-5 py-3">
                  <span
                    className={
                      k.can_write
                        ? 'rounded-pill bg-warning-50 px-2 py-0.5 text-[10px] font-semibold text-warning-700'
                        : 'rounded-pill bg-n-100 px-2 py-0.5 text-[10px] font-semibold text-n-600'
                    }
                  >
                    {k.can_write ? 'LEITURA + ESCRITA' : 'SÓ LEITURA'}
                  </span>
                </td>
                <td className="px-5 py-3 text-[12px] text-n-600">{k.dono ?? '—'}</td>
                <td className="px-5 py-3 text-[12px] text-n-500">
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleString('pt-BR') : 'nunca'}
                </td>
                <td className="px-5 py-3 text-right">
                  {!k.revoked_at && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Revogar a chave "${k.name}"?\n\nQuem a usa perde o acesso na hora. Não dá pra desfazer.`)) {
                          revokeMut.mutate(k);
                        }
                      }}
                      className="rounded-md p-1.5 text-n-500 hover:bg-danger-50 hover:text-danger-600"
                      title="Revogar"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <CreateKeyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(k) => {
          setModalOpen(false);
          setCriada(k);
        }}
      />
      <ShowTokenModal chave={criada} onClose={() => setCriada(null)} />
    </div>
  );
}

/* ─── Modal criar chave ──────────────────────────────────────────── */

function CreateKeyModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (k: CreatedMcpKey) => void;
}) {
  const [name, setName] = useState('');
  const [tipo, setTipo] = useState<'master' | 'cliente'>('master');
  const [serverId, setServerId] = useState<number | ''>('');
  const [tenantId, setTenantId] = useState('');
  const [canWrite, setCanWrite] = useState(false);
  const qc = useQueryClient();

  const serversQuery = useQuery({ queryKey: ['servers'], queryFn: () => serversApi.list(), enabled: open });
  const tenantsQuery = useQuery({
    queryKey: ['server-tenants', serverId],
    queryFn: () => serversApi.tenants(serverId as number),
    enabled: open && tipo === 'cliente' && serverId !== '',
  });

  const reset = (): void => {
    setName('');
    setTipo('master');
    setServerId('');
    setTenantId('');
    setCanWrite(false);
  };

  const mut = useMutation({
    mutationFn: async () =>
      mcpApi.create({
        name,
        can_write: canWrite,
        ...(tipo === 'cliente' && { server_id: serverId as number, tenant_id: Number(tenantId) }),
      }),
    onSuccess: (k) => {
      void qc.invalidateQueries({ queryKey: ['mcp-keys'] });
      reset();
      onCreated(k);
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const pronto = name.trim().length > 0 && (tipo === 'master' || (serverId !== '' && tenantId !== ''));

  return (
    <Modal open={open} onClose={onClose} title="Nova chave do MCP" width={520}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (pronto) mut.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <Input
          label="Nome"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ex.: Claude do João"
          hint="Pra você saber de quem é quando aparecer na auditoria."
          autoFocus
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[12px] font-medium text-n-700">Escopo</legend>
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-n-200 p-3 has-[:checked]:border-brand-500 has-[:checked]:bg-brand-50">
            <input type="radio" name="tipo" checked={tipo === 'master'} onChange={() => setTipo('master')} className="mt-0.5" />
            <span>
              <span className="block text-[13px] font-medium text-n-900">Master — todos os clientes</span>
              <span className="block text-[11px] text-n-500">
                Pra equipe ChatPlus. Enxerga e opera qualquer cliente de qualquer servidor.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-n-200 p-3 has-[:checked]:border-brand-500 has-[:checked]:bg-brand-50">
            <input type="radio" name="tipo" checked={tipo === 'cliente'} onChange={() => setTipo('cliente')} className="mt-0.5" />
            <span>
              <span className="block text-[13px] font-medium text-n-900">De cliente — um só</span>
              <span className="block text-[11px] text-n-500">Presa a um servidor e um cliente. Não vê os outros.</span>
            </span>
          </label>
        </fieldset>

        {tipo === 'cliente' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-n-700">Servidor</span>
              <select
                className="h-[36px] rounded-md border border-n-200 bg-white px-2 text-[13px] text-n-900"
                value={serverId}
                onChange={(e) => {
                  setServerId(e.target.value ? Number(e.target.value) : '');
                  setTenantId('');
                }}
              >
                <option value="">Selecione…</option>
                {(serversQuery.data ?? [])
                  .filter((s) => s.status === 'active')
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-n-700">Cliente</span>
              <select
                className="h-[36px] rounded-md border border-n-200 bg-white px-2 text-[13px] text-n-900"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                disabled={serverId === '' || tenantsQuery.isLoading}
              >
                <option value="">{tenantsQuery.isLoading ? 'Carregando…' : 'Selecione…'}</option>
                {(tenantsQuery.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (#{t.id})
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-n-200 p-3">
          <input type="checkbox" checked={canWrite} onChange={(e) => setCanWrite(e.target.checked)} className="mt-0.5" />
          <span>
            <span className="block text-[13px] font-medium text-n-900">Permitir escrita</span>
            <span className="block text-[11px] text-n-500">
              Criar cliente e configurar plano (só master), criar usuário/departamento, editar e converter canal,
              criar e disparar campanha. Sem marcar, a IA só consulta.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mut.isPending} disabled={!pronto}>
            Criar chave
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Modal que mostra a chave UMA vez ───────────────────────────── */

function ShowTokenModal({ chave, onClose }: { chave: CreatedMcpKey | null; onClose: () => void }) {
  const [copiado, setCopiado] = useState<'token' | 'config' | null>(null);

  if (!chave) return null;

  const config = JSON.stringify(
    {
      mcpServers: {
        chatplus: {
          type: 'http',
          url: MCP_URL,
          headers: { Authorization: `Bearer ${chave.token}` },
        },
      },
    },
    null,
    2,
  );

  const copiar = async (o: 'token' | 'config'): Promise<void> => {
    await navigator.clipboard.writeText(o === 'token' ? chave.token : config);
    setCopiado(o);
    setTimeout(() => setCopiado(null), 1500);
  };

  return (
    <Modal open onClose={onClose} title={`Chave criada — ${chave.name}`} width={600}>
      <div className="flex flex-col gap-4">
        <div className="rounded-md bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
          Esta é a <b>única vez</b> que a chave aparece. Copie agora e passe pelo canal seguro. Perdeu? Revogue e crie
          outra.
        </div>

        <div>
          <div className="mb-1 text-[12px] font-medium text-n-700">Chave</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-md border border-n-200 bg-n-50 px-3 py-2 font-mono text-[12px] text-n-900">
              {chave.token}
            </code>
            <Button variant="outline" size="sm" onClick={() => void copiar('token')}>
              {copiado === 'token' ? <Check size={12} /> : <Copy size={12} />}
            </Button>
          </div>
        </div>

        <div className="rounded-md border border-n-200 bg-n-50 px-3 py-2 text-[12px] text-n-700">
          <div className="mb-1 font-medium">Claude Desktop / claude.ai (Conectores)</div>
          Configurações → Conectores → <b>Adicionar conector personalizado</b> → URL{' '}
          <code className="font-mono text-brand-600">{MCP_URL}</code> → <b>Conectar</b>. Vai abrir uma página
          pedindo a chave: cole a de cima. Só isso — sem OAuth Client ID.
        </div>

        <div>
          <div className="mb-1 text-[12px] font-medium text-n-700">
            Claude Code (<span className="font-mono">.mcp.json</span>) e clientes que aceitam header
          </div>
          <div className="relative">
            <pre className="overflow-x-auto rounded-md border border-n-200 bg-n-50 p-3 font-mono text-[11px] text-n-800">
              {config}
            </pre>
            <div className="absolute right-2 top-2">
              <Button variant="outline" size="sm" onClick={() => void copiar('config')}>
                {copiado === 'config' ? <Check size={12} /> : <Copy size={12} />}
              </Button>
            </div>
          </div>
        </div>

        <div className="text-[11px] text-n-500">
          Escopo: <b>{chave.master ? 'master (todos os clientes)' : 'um cliente'}</b> ·{' '}
          <b>{chave.can_write ? 'leitura + escrita' : 'só leitura'}</b>
        </div>

        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>Copiei, pode fechar</Button>
        </div>
      </div>
    </Modal>
  );
}
